import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Paper, Stack, Chip, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip,
  Snackbar, Alert,
} from '@mui/material';
import {
  CheckCircleOutlined, ArrowForwardOutlined, ErrorOutlined,
  LinkOutlined,
  RefreshOutlined, Add as AddIcon, OpenInNewOutlined,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  integrationsOverviewApi, connectionsApi, syncLogsApi,
  type IntegrationsOverview as OverviewData, type ConnectionResponse, type SyncLogResponse,
} from '../../api/integrations';
import { getProvider } from './integrationCatalog';
import SetupGuide from '../../components/onboarding/SetupGuide';
import { useTranslation } from 'react-i18next';
import { activeLocale } from '../../utils/datetime';

type Toast = { message: string; severity: 'success' | 'error' | 'info' } | null;

const IntegrationsOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);
  const [syncLogs, setSyncLogs] = useState<SyncLogResponse[]>([]);
  const [toast, setToast] = useState<Toast>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [ov, conns, logs] = await Promise.all([
        integrationsOverviewApi.get(),
        connectionsApi.list(),
        syncLogsApi.list(),
      ]);
      setOverview(ov);
      setConnections(conns);
      setSyncLogs(logs);
    } catch (e) { console.warn('Failed to load integrations overview', e); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const stats = [
    { label: t('integrations:overview.stats.activeConnections'), value: overview?.active_connections ?? 0, color: 'primary.main', path: '/integrations/connectors' },
    { label: t('integrations:overview.stats.activeWebhooks'), value: overview?.active_webhooks ?? 0, color: 'success.main', path: '/integrations/webhooks' },
    { label: t('integrations:overview.stats.apiKeys'), value: overview?.active_api_keys ?? 0, color: 'text.primary', path: '/integrations/api' },
    { label: t('integrations:overview.stats.apiCalls30d'), value: overview?.total_api_calls_30d ?? 0, color: 'info.main', path: '/integrations/api' },
  ];

  const statusChip = (status: string | undefined) => {
    if (status === 'active') return <Chip icon={<CheckCircleOutlined sx={{ fontSize: 12 }} />} label={t('integrations:overview.statusHealthy')} size="small" color="success" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />;
    return <Chip icon={<ErrorOutlined sx={{ fontSize: 12 }} />} label={t('integrations:overview.statusError')} size="small" color="error" variant="outlined" sx={{ borderRadius: 1, height: 20, fontSize: '0.65rem' }} />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('integrations:overview.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('integrations:overview.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}><IconButton size="small" onClick={fetchAll} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#ffffff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => navigate('/integrations/connectors')}
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('integrations:overview.addConnection')}</Button>
          {/* HANDOFF_2026-05-25 Bug 26 — top-right View docs button. */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNewOutlined />}
            component="a"
            href="https://svdy.com/integrations"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem', color: 'text.primary', borderColor: 'rgba(0,0,0,0.2)', bgcolor: '#ffffff', '&:hover': { borderColor: 'rgba(0,0,0,0.35)', bgcolor: '#ffffff' } }}
          >
            {t('integrations:overview.viewDocs')}
          </Button>
        </Stack>
      </Box>

      {/* Setup Guide — backend-driven (ADR-019 §D8) */}
      <SetupGuide section="integrations" />

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {stats.map(s => (
          <Grid item xs={6} md={3} key={s.label}>
            <Paper elevation={0} onClick={() => navigate(s.path)} sx={{
              p: 1.5, borderRadius: 2, cursor: 'pointer', border: '1px solid', borderColor: 'divider', boxShadow: 'none',
              transition: 'border-color 120ms ease', '&:hover': { borderColor: s.color },
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary', display: 'block', mb: 0.5 }}>{s.label}</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, color: s.color, lineHeight: 1 }}>{typeof s.value === 'number' ? s.value.toLocaleString(activeLocale()) : s.value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Connection Health + Recent Sync */}
      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.25 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('integrations:overview.connectionHealth')}</Typography>
            </Box>
            {connections.length === 0 ? (
              <Box sx={{ px: 2.5, pb: 2 }}><Typography variant="caption" color="text.secondary">{t('integrations:overview.noConnections')}</Typography></Box>
            ) : (
              <Stack sx={{ px: 2.5, pb: 2 }} spacing={0.5}>
                {connections.map(c => {
                  const provider = getProvider(c.provider);
                  return (
                    <Box key={c.id} onClick={() => navigate('/integrations/connectors')} sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.25, borderRadius: 1.5,
                      cursor: 'pointer', transition: 'background-color 80ms ease', '&:hover': { bgcolor: 'grey.50' },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <LinkOutlined sx={{ fontSize: 18, color: c.status === 'error' ? 'error.main' : 'primary.main' }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{provider?.nameKey ? t(provider.nameKey) : c.provider}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                            {provider?.category.replace('_', ' & ')} · {c.last_sync_at ? t('integrations:overview.syncedAt', { time: new Date(c.last_sync_at).toLocaleTimeString(activeLocale()) }) : t('integrations:overview.neverSynced')}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {statusChip(c.status)}
                        <ArrowForwardOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ height: '100%', borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.25, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>{t('integrations:overview.recentSyncActivity')}</Typography>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={() => navigate('/integrations/connectors')}>{t('integrations:overview.viewAll')}</Typography>
            </Box>
            {syncLogs.length === 0 ? (
              <Box sx={{ px: 2.5, pb: 2 }}><Typography variant="caption" color="text.secondary">{t('integrations:overview.noSyncActivity')}</Typography></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>{t('integrations:overview.tableHeaders.event')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>{t('integrations:overview.tableHeaders.records')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>{t('integrations:overview.tableHeaders.time')}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.8rem', py: 0.75 }}>{t('integrations:overview.tableHeaders.status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {syncLogs.slice(0, 8).map(s => (
                      <TableRow key={s.id} hover>
                        <TableCell><Typography variant="caption" sx={{ fontWeight: 600 }}>{s.event || '—'}</Typography></TableCell>
                        <TableCell align="center"><Typography variant="caption" sx={{ fontWeight: 600 }}>{s.records_count}</Typography></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{s.created_at ? new Date(s.created_at).toLocaleString(activeLocale()) : '—'}</Typography></TableCell>
                        <TableCell align="center">
                          <Chip label={s.status === 'completed' ? t('integrations:overview.statusOk') : t('integrations:overview.statusFailed')} size="small"
                            color={s.status === 'completed' ? 'success' : 'error'} variant="outlined"
                            sx={{ borderRadius: 1, height: 18, fontSize: '0.65rem' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Toast */}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast?.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default IntegrationsOverview;
